/**
 * ImageResizeDemoPanel — C080 이미지 처리 데모 (queue #646 §R646-2 항목9)
 * 사용자 업로드 이미지(★선택 — 미첨부 시 BE 가 번들 샘플 이미지 사용) + 목표 폭/높이 입력 →
 * 리사이즈 결과 PNG 인라인 렌더 + 원본/결과 크기. 순수 변환.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImageResize } from '../../hooks/useImageResizeDemo';
import { cardStyle, sectionTitleStyle, subtitleStyle, labelStyle, inputStyle, monoStyle, primaryButtonStyle, DemoSafetyBanner, DemoErrorState, DemoEmptyHint } from './demoShared';

export function ImageResizeDemoPanel() {
  const { t } = useTranslation();
  const resizeMutation = useImageResize();
  const [file, setFile] = useState<File | null>(null);
  const [width, setWidth] = useState(200);
  const [height, setHeight] = useState(200);

  function handleResize() {
    resizeMutation.mutate({ file, width, height });
  }

  return (
    <section aria-labelledby="image-panel-title">
      <h2 id="image-panel-title" style={sectionTitleStyle}>{t('showcase.demo.image.title')}</h2>
      <p style={subtitleStyle}>{t('showcase.demo.image.subtitle')}</p>

      <DemoSafetyBanner text={t('showcase.demo.image.banner')} />

      <div style={cardStyle}>
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label htmlFor="image-file" style={labelStyle}>{t('showcase.demo.image.fileLabel')}</label>
          <input
            id="image-file"
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
          <div>
            <label htmlFor="image-width" style={labelStyle}>{t('showcase.demo.image.widthLabel')}</label>
            <input id="image-width" type="number" min={1} max={2000} value={width} onChange={(e) => setWidth(Number(e.target.value))} style={inputStyle} />
          </div>
          <div>
            <label htmlFor="image-height" style={labelStyle}>{t('showcase.demo.image.heightLabel')}</label>
            <input id="image-height" type="number" min={1} max={2000} value={height} onChange={(e) => setHeight(Number(e.target.value))} style={inputStyle} />
          </div>
        </div>

        <button
          type="button"
          onClick={handleResize}
          disabled={resizeMutation.isPending}
          aria-busy={resizeMutation.isPending}
          style={{ ...primaryButtonStyle, opacity: resizeMutation.isPending ? 0.6 : 1 }}
        >
          {resizeMutation.isPending ? t('showcase.demo.image.resizing') : t('showcase.demo.image.resizeButton')}
        </button>

        <div style={{ marginTop: 'var(--spacing-4)' }}>
          {resizeMutation.isError && (
            <DemoErrorState title={t('showcase.demo.common.callFailed')} error={resizeMutation.error} />
          )}
          {!resizeMutation.isError && !resizeMutation.data && <DemoEmptyHint text={t('showcase.demo.image.empty')} />}
          {resizeMutation.data && !resizeMutation.isError && (
            <div>
              <img
                src={`data:${resizeMutation.data.contentType};base64,${resizeMutation.data.base64}`}
                alt={t('showcase.demo.image.resultAlt')}
                style={{ border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-surface-background)', padding: 'var(--spacing-3)', maxWidth: '100%' }}
              />
              <p style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', ...monoStyle }}>
                {t('showcase.demo.image.originalSizeLabel')}: {resizeMutation.data.originalWidth}×{resizeMutation.data.originalHeight}px
                {' · '}
                {t('showcase.demo.image.resultSizeLabel')}: {resizeMutation.data.resultWidth}×{resizeMutation.data.resultHeight}px
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
